import { Avatar, Badge, Card, Typography } from '../ui';
import { needsAttention, repliedUnanswered, shortRelativeTime, rowSubtitle, strippedMessageText } from '../../../lib/contact-rows';
import { ChannelChip } from './ChannelChip';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { useGenericBi } from '../../../lib/use-generic-bi';
import type { WebContact } from '../../../lib/contact-book';
import { useTranslation } from '../../../lib/i18n';
import styles from './contacts.module.css';

/**
 * One person in the list.
 *
 * Everything shown here comes off the entry the list already holds — nothing is fetched per row, and
 * nothing is computed that `contact-rows` does not compute identically on iOS and Android.
 */
export function ContactRow({
  entry,
  onOpen,
  isNew = false,
}: {
  entry: WebContact;
  onOpen: (personId: string) => void;
  /** New correspondence since this device last opened the person: the dot. */ isNew?: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const card = entry.contact;
  const personId = card?.personId ?? '';
  // The server's own summary, verbatim. "Waiting on you" is the ACTION REQUIRED badge now, not a subtitle —
  // saying it twice on one row was noise.
  const subtitle = rowSubtitle(entry);
  // The last thing said, and on which channel: the newest thread across both directions.
  const newest = [...(entry.outbound ?? []), ...(entry.inbound ?? [])].sort(
    (a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime(),
  )[0];
  const preview = newest ? strippedMessageText(newest.lastMessage) : '';
  // A contact can genuinely have no display name — an outreach counterparty the people book only
  // knows by address. It rendered as a blank line beside an empty circle, which reads as broken
  // rather than as "unnamed", so fall back to how we can actually reach them, then to a label.
  const displayName: string = card?.displayName?.trim() || entry.channels?.find((channel) => channel.value)?.value || t('contacts.row.unnamed') || '';

  return (
    <Card
      variant="outline"
      size="sm"
      // The whole card is the hit target, so it IS the button — a hand-rolled one here would be
      // outside the Card spec, and a button inside a card-as-button is invalid HTML.
      component="button"
      interactive
      className={styles.row}
      onClick={() => {
        if (!personId) return;
        logPageClickBi({ page_name: 'contacts_page', button_name: 'open_contact' });
        onOpen(personId);
      }}
      // A row for somebody with no Person behind them can be shown but not opened — the detail is
      // keyed on a person id there is none of. It stays readable rather than being hidden.
      // `CardProps` extends HTMLAttributes and carries no `disabled`, so the button element gets it
      // through the DOM prop the polymorphic `component="button"` actually renders.
      aria-disabled={!personId}
      data-new={isNew || undefined}
    >
      {/* The dot: there is something here the reader has not seen. The badge below says whether it also
          needs an answer. */}
      {isNew ? <span className={styles.newDot} role="img" aria-label={t('contacts.row.new')} /> : null}
      <Avatar
        initials={avatarInitials(displayName)}
        // The photo symphony-bff resolved for this contact (override → recipient self-photo → CRM);
        // absent falls back to the initials disc below.
        photoUrl={card?.effectiveAvatarUrl}
        size={48}
        // Seeded on the person so the same contact keeps one colour across sessions and screens.
        color={avatarSlotFor(personId || card?.displayName || '')}
      />
      <span className={styles.rowMain}>
        <span className={styles.rowTop}>
          <Typography variant="p2" className={styles.rowName}>
            {displayName}
          </Typography>
          {needsAttention(entry) ? (
            <Badge variant="waiting-on-you" label={t('contacts.row.actionRequired')} className={styles.rowBadge} />
          ) : repliedUnanswered(entry) ? (
            <Badge variant="connected" label={t('contacts.row.replied')} className={styles.rowBadge} />
          ) : null}
          <Typography variant="p3" color="secondary" className={styles.rowAge}>
            {shortRelativeTime(entry.lastActivityAt)}
          </Typography>
        </span>
        {preview ? (
          <span className={styles.rowPreview}>
            <ChannelChip channel={newest?.channel} />
            <Typography variant="p3" color="secondary" className={styles.rowSubtitle} dir="auto">
              {preview}
            </Typography>
          </span>
        ) : null}
        {subtitle ? (
          <Typography
            variant="p3"
            // Secondary ink, NOT the accent. The accent is the brand neon: unreadable on the light
            // surface and shouting on the dark one. It also said nothing — every row inside "Needs
            // attention" carried it, so the colour marked the section, not the row. The section
            // header already does that, and `rowSubtitle` distinguishes the two cases by WORDS.
            color="secondary"
            className={styles.rowSubtitle}
          >
            {subtitle}
          </Typography>
        ) : null}
        {card?.labels?.length ? (
          <span className={styles.labels}>
            {card.labels.map((label) => (
              // The DS's one outlined Style. A label is a category with no status of its own, which
              // is exactly the case that Style covers — naming the DS style, not a profession.
              <Badge key={label} variant="agent-profession" label={label} />
            ))}
          </span>
        ) : null}
      </span>
    </Card>
  );
}
